import { CodeHighlight } from "@mantine/code-highlight";
import { Code } from "@mantine/core";
import { Component, type ReactNode } from "react";

interface CodeBlockProps {
  content?: string;
  language?: string;
}

interface CodeHighlightBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface CodeHighlightBoundaryState {
  failed: boolean;
}

/**
 * CodeHighlightBoundary degrades a code block to plain text when its highlighter
 * throws. shiki throws synchronously for a grammar it has not loaded, and without
 * a boundary that single throw unmounts the whole page. The boundary renders the
 * unhighlighted fallback instead, so an unknown language costs only its own
 * highlighting, never the surrounding page.
 */
class CodeHighlightBoundary extends Component<
  CodeHighlightBoundaryProps,
  CodeHighlightBoundaryState
> {
  override state: CodeHighlightBoundaryState = { failed: false };

  static getDerivedStateFromError(): CodeHighlightBoundaryState {
    return { failed: true };
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

/**
 * CodeBlock renders a fenced code block (or a standalone {% code-block %}) through
 * Mantine `CodeHighlight`, with a copy button.
 *
 * `CodeHighlight` resolves its highlighter from a `CodeHighlightAdapterProvider`
 * higher in the tree; the docs app supplies one (shiki). A block whose language
 * the highlighter has not loaded makes shiki throw, so the highlighter is wrapped
 * in an error boundary that falls back to plain, unhighlighted code.
 */
export const CodeBlock = ({ content = "", language }: CodeBlockProps) => (
  <CodeHighlightBoundary
    fallback={
      <Code block my="md">
        {content}
      </Code>
    }
  >
    <CodeHighlight code={content} language={language} my="md" withCopyButton />
  </CodeHighlightBoundary>
);
